import { CheckIcon } from "@chakra-ui/icons";
import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  Flex,
} from "@chakra-ui/react";
import PlusIcon from "@root/assets/icons/plusicon";
import { ROUTE_NAMES } from "@root/constants";
import { useMainChatContext } from "@root/context/kps-chat-context";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BG_OVERLAY } from "@root/hooks/useTourGuide";
import { useLegalChatbotTourGuide } from "@root/hooks/useLegalChatbotTourGuide";
import { useProjectDocumentChatbotTourGuide } from "@root/hooks/useProjectDocumentChatbotTourGuide";
import { useTourGuideContext } from "@root/context/tour-guide-context";

type DropdownItem = {
  labelKey: string; // translation key instead of pre-translated string
  icon: React.ElementType;
  link?: string;
};

type ChatbotDropdownProps = {
  items: DropdownItem[];
  onUploadClick?: () => void;
  onMenuClose?: () => void;
};

export default function ChatbotDropdown({
  items,
  onUploadClick,
  onMenuClose,
}: ChatbotDropdownProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const { docChatMode, selectedProject, attachedFile } = useMainChatContext();
  const { t } = useTranslation("common");

  const isProjectChat =
    (pathname === ROUTE_NAMES.DOCUMENT_INTEGRATION_CHATBOT ||
      pathname.includes("/document-chat")) &&
    !!selectedProject &&
    attachedFile?.length < 1;

  const isChecklist = pathname.includes("/checklist");
  const isLegalChatbot =
    pathname.startsWith(ROUTE_NAMES.LEGAL_CHATBOT) ||
    pathname.startsWith(ROUTE_NAMES.KSCE_CHAT_SCREEN);

  // Tour guide setup
  const legalChatbotTour = useLegalChatbotTourGuide();
  const projectDocumentChatbotTour = useProjectDocumentChatbotTourGuide();
  const { activeTourModule, currentTourStep } = useTourGuideContext();

  // Get the tour config to access steps
  const tourConfig = isLegalChatbot
    ? legalChatbotTour.legalChatbotTourConfig
    : projectDocumentChatbotTour.projectDocumentChatbotTourConfig;

  // Use context to check if tour is active (shared state across components)
  const isTourActive = isLegalChatbot
    ? activeTourModule === "legalChatbot"
    : activeTourModule === "projectDocumentChatbot";

  // Compute currentStepData from context's currentTourStep (shared state)
  const currentStepDataForCheck =
    isTourActive &&
      tourConfig.steps.length > 0 &&
      currentTourStep >= 0 &&
      currentTourStep < tourConfig.steps.length
      ? tourConfig.steps[currentTourStep]
      : null;

  console.log("tourguide: ", isTourActive);

  const handleMenuClick = (item: DropdownItem) => {
    // Legal Chatbot "Upload"
    if (
      item.labelKey === "contact.upload" &&
      (isLegalChatbot || pathname === ROUTE_NAMES.DRAWING_ANALYSIS_DASHBOARD) &&
      onUploadClick
    ) {
      onUploadClick();
    }
    // Project/Drawing Analysis "Select Files"
    else if (
      item.labelKey === "translate.selectFile" &&
      (isProjectChat || isChecklist) &&
      onUploadClick
    ) {
      onUploadClick();
    }
    // Navigate if link exists and not current
    // Check if we're already on a related chat route
    const isOnRelatedChatRoute =
      // Drawing analysis
      (item.link === ROUTE_NAMES.DRAWING_ANALYSIS_DASHBOARD &&
        pathname.startsWith(ROUTE_NAMES.DRAWING_ANALYSIS)) ||
      // Checklist chat → multi document chatbot
      (item.link === ROUTE_NAMES.CHECKLIST &&
        (pathname.includes(ROUTE_NAMES.CHECKLIST_CHAT) ||
          pathname.startsWith("/checklist-chat"))) ||
      // Document chat → project document chatbot
      (item.link === ROUTE_NAMES.DOCUMENT_INTEGRATION_CHATBOT &&
        (pathname.includes(ROUTE_NAMES.DOCUMENT_CHAT) ||
          pathname.startsWith("/document-chat"))) ||
      // Legal chat → legal chatbot
      (item.link === ROUTE_NAMES.LEGAL_CHATBOT &&
        (pathname === ROUTE_NAMES.KSCE_CHAT_SCREEN ||
          (pathname.startsWith("/chat/") &&
            !pathname.includes("/kcs-chat")))) ||
      // KCS chat → kcs chatbot
      (item.link === ROUTE_NAMES.KCS &&
        (pathname.includes("/kcs-chat") || pathname.startsWith("/kcs-chat")));

    if (item.link && item.link !== pathname && !isOnRelatedChatRoute) {
      navigate(item.link);
    }
  };

  // Check if current step matches - use manually computed currentStepDataForCheck
  const isCurrentStep =
    isTourActive &&
    (currentStepDataForCheck?.id === "chatbot-dropdown" ||
      currentStepDataForCheck?.id === "project-chatbot-dropdown");

  return (
    <Menu
      onClose={() => {
        // Call onMenuClose callback when menu closes to reset tooltip hover state
        onMenuClose?.();
      }}
    >
      <MenuButton
        as={Button}
        w="fit-content"
        h="fit-content"
        p="2"
        borderRadius="full"
        display="flex"
        alignItems="center"
        justifyContent="center"
        _hover={{ bg: "gray.100" }}
        _active={{ bg: "#D9D9D9" }}
        data-tour-item="chatbot-dropdown"
        // bg={isCurrentStep ? BG_OVERLAY.BACKGROUND : "transparent"}
        // backdropFilter={isCurrentStep ? BG_OVERLAY.BACKDROP_FILTER : "none"}
        bg="transparent"
      >
        <Flex w="fit-content" px="2px">
          <PlusIcon color="#919191" />
        </Flex>
      </MenuButton>

      <MenuList borderRadius="lg" shadow="lg" p={1} width="256px">
        {items?.map((item, index) => {
          // Check if current route matches the item link
          // Handle exact matches
          let isCurrent = item.link === pathname;

          // Handle drawing analysis routes
          if (
            item.link === ROUTE_NAMES.DRAWING_ANALYSIS_DASHBOARD &&
            pathname.startsWith(ROUTE_NAMES.DRAWING_ANALYSIS)
          ) {
            isCurrent = true;
          }

          // Handle chat routes matching their dashboard routes
          // If on checklist-chat, disable multi document chatbot
          if (
            item.link === ROUTE_NAMES.CHECKLIST &&
            (pathname.includes(ROUTE_NAMES.CHECKLIST_CHAT) ||
              pathname.startsWith("/checklist-chat"))
          ) {
            isCurrent = true;
          }

          // If on document-chat, disable project document chatbot
          if (
            item.link === ROUTE_NAMES.DOCUMENT_INTEGRATION_CHATBOT &&
            (pathname.includes(ROUTE_NAMES.DOCUMENT_CHAT) ||
              pathname.startsWith("/document-chat"))
          ) {
            isCurrent = true;
          }

          if (
            item.link === ROUTE_NAMES.LEGAL_CHATBOT &&
            (pathname === ROUTE_NAMES.KSCE_CHAT_SCREEN ||
              (pathname.startsWith("/chat/") &&
                !pathname.includes("/kcs-chat")))
          ) {
            isCurrent = true;
          }

          // If on kcs-chat, disable kcs chatbot
          if (
            item.link === ROUTE_NAMES.KCS &&
            (pathname.includes("/kcs-chat") || pathname.startsWith("/kcs-chat"))
          ) {
            isCurrent = true;
          }

          const isDisabled =
            isCurrent ||
            (item.labelKey === "contact.upload" &&
              !isLegalChatbot &&
              pathname !== ROUTE_NAMES.DRAWING_ANALYSIS_DASHBOARD) ||
            (item.labelKey === "translate.selectFile" &&
              !(isProjectChat || isChecklist));

          return (
            <MenuItem
              key={index}
              icon={<item.icon size={18} />}
              onClick={() => handleMenuClick(item)}
              isDisabled={isDisabled}
            >
              <Flex align="center" justify="space-between" width="100%">
                <span>{t(item.labelKey)}</span>
                {isCurrent && <CheckIcon color="#1A1A1A" boxSize={4} />}
              </Flex>
            </MenuItem>
          );
        })}
      </MenuList>
    </Menu>
  );
}
